import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { getPatientRecord } from '../lib/api';
import { canPrintEvolutionNote, createAndOpenEvolutionNote } from '../lib/clinicalEncounterNav';
import IntakeWizard from '../components/clinical/IntakeWizard';
import ClinicalAlertsBanner from '../components/clinical/ClinicalAlertsBanner';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, ClipboardList, FileText, Plus } from 'lucide-react';
import { formatAppointmentDateOnlyInClinic } from '../lib/clinicTime';
import { useClinicBranding } from '../context/ClinicBrandingContext';
import { printEvolutionNote } from '../lib/printEvolutionNote';

function safeEncounterDate(enc) {
  const dateSrc = enc?.encounter_date || enc?.created_at;
  if (!dateSrc) return '';
  const raw = String(dateSrc);
  const ymd = raw.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '';
  try {
    return formatAppointmentDateOnlyInClinic(ymd, 'd MMM yyyy');
  } catch {
    return ymd;
  }
}

export default function AdminPatientRecordDetail() {
  const { recordId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const appointmentId = searchParams.get('appointment');
  const tab = searchParams.get('tab') === 'encuentros' ? 'encuentros' : 'historia';
  const navigate = useNavigate();
  const { info: clinicInfo } = useClinicBranding();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printingId, setPrintingId] = useState(null);

  const setTab = (next) => {
    const sp = new URLSearchParams(searchParams);
    if (next === 'encuentros') {
      sp.set('tab', 'encuentros');
    } else {
      sp.delete('tab');
    }
    setSearchParams(sp, { replace: true });
  };

  const reload = useCallback(async () => {
    if (!recordId) return;
    setLoading(true);
    try {
      const d = await getPatientRecord(recordId);
      setData(d);
    } catch {
      toast.error('No se pudo cargar el expediente');
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const record = data?.record;
  const encounters = Array.isArray(data?.encounters) ? data.encounters : [];
  const patient = record?.patient;

  const startEncounterForAppointment = async () => {
    if (!appointmentId) return;
    try {
      const ok = await createAndOpenEvolutionNote(recordId, navigate, { appointmentId });
      if (!ok) toast.error('No se pudo crear la nota de evolución');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la nota de evolución');
    }
  };

  const startNewEvolutionNote = async () => {
    try {
      const ok = await createAndOpenEvolutionNote(recordId, navigate);
      if (!ok) toast.error('No se pudo crear la nota de evolución');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la nota de evolución');
    }
  };

  const handlePrintEncounter = async (enc) => {
    if (!enc?.id || !canPrintEvolutionNote(enc)) {
      toast.error('Esta nota no tiene contenido para imprimir');
      return;
    }
    setPrintingId(enc.id);
    try {
      const result = await printEvolutionNote(enc.id, { clinicInfo });
      if (!result.ok) {
        if (result.reason === 'empty_content') {
          toast.error(result.message || 'La nota no tiene contenido guardado');
        } else {
          toast.error('Permita ventanas emergentes para abrir el PDF.');
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo preparar la impresión');
    } finally {
      setPrintingId(null);
    }
  };

  const encounterForAppt = encounters.find((e) => e.appointment_id === appointmentId);

  if (loading && !record) {
    return <p className="text-muted-foreground py-12 text-center">Cargando expediente…</p>;
  }

  if (!record) {
    return <p className="text-muted-foreground py-12 text-center">Expediente no encontrado</p>;
  }

  return (
    <div className="space-y-6 min-h-[50vh]">
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/expedientes">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Expedientes
          </Link>
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl font-bold truncate">
            {patient?.full_name || 'Paciente'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {[patient?.phone, patient?.email].filter(Boolean).join(' · ') || 'Sin contacto'}
          </p>
        </div>
        <Badge variant={record.status === 'complete' ? 'default' : 'secondary'}>
          {record.status === 'complete' ? 'Historia completa' : 'Borrador'}
        </Badge>
      </div>

      <ClinicalAlertsBanner intake={record?.intake_adult} />

      {appointmentId ? (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="py-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm">
              Vinculado a una cita.{' '}
              {encounterForAppt
                ? 'Ya existe una nota de evolución para esta visita.'
                : 'Inicie la nota de evolución de esta visita.'}
            </p>
            {encounterForAppt ? (
              <Button
                size="sm"
                className="rounded-full"
                onClick={() =>
                  navigate(`/admin/expedientes/${recordId}/encuentro/${encounterForAppt.id}`)
                }
              >
                Abrir nota de la cita
              </Button>
            ) : (
              <Button size="sm" className="rounded-full gap-1" onClick={startEncounterForAppointment}>
                <Plus className="w-4 h-4" />
                Iniciar nota de evolución
              </Button>
            )}
          </CardContent>
        </Card>
      ) : null}

      <div className="flex flex-wrap gap-2 border-b border-border pb-2">
        <Button
          type="button"
          variant={tab === 'historia' ? 'default' : 'ghost'}
          size="sm"
          className="rounded-full gap-1"
          onClick={() => setTab('historia')}
        >
          <ClipboardList className="w-4 h-4" />
          Historia clínica
        </Button>
        <Button
          type="button"
          variant={tab === 'encuentros' ? 'default' : 'ghost'}
          size="sm"
          className="rounded-full gap-1"
          onClick={() => setTab('encuentros')}
        >
          <Calendar className="w-4 h-4" />
          Notas de evolución ({encounters.length})
        </Button>
      </div>

      {tab === 'historia' ? (
        <IntakeWizard
          recordId={recordId}
          patientId={record.patient_id}
          patient={patient}
          initialIntake={record.intake_adult}
          initialConsent={record.consent}
          recordStatus={record.status}
          onSaved={() => reload()}
          onCompleted={() => setTab('encuentros')}
        />
      ) : (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Notas de evolución</CardTitle>
            <Button size="sm" className="rounded-full gap-1" onClick={startNewEvolutionNote}>
              <Plus className="w-4 h-4" />
              Nueva nota de evolución
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {encounters.length === 0 ? (
              <p className="text-muted-foreground text-sm">No hay notas de evolución registradas.</p>
            ) : (
              encounters.map((enc) => (
                <div
                  key={enc.id || enc.created_at}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3"
                >
                  <div>
                    <p className="font-medium text-sm">
                      {enc.encounter_type === 'intake' ? 'Historia inicial' : 'Nota de evolución'}
                      {enc.folio ? ` · ${enc.folio}` : ''}
                      {enc.appointment_id ? ' · cita vinculada' : ''}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {safeEncounterDate(enc)}
                      {' · '}
                      {enc.status === 'complete' ? 'Completa' : 'Borrador'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {canPrintEvolutionNote(enc) ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={printingId === enc.id}
                        onClick={() => handlePrintEncounter(enc)}
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        {enc.status === 'complete' ? 'Reimprimir PDF' : 'Ver / Imprimir PDF'}
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/admin/expedientes/${recordId}/encuentro/${enc.id}`)}
                    >
                      Abrir
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
